import { enrichImageObject, getPublicFirestoreUrl } from './encoding-helpers';
import { memberLevelMap } from '../models/member-client-enums.js';

const DEFAULT_COLOR_PRIMARY = '#E2B013';

/**
 * Enriches the member with additional properties for display purposes.
 * 
 * @param {Object} member - Required. The member object to enrich.
 * @param {Object} refPathMap - Optional, but recommended. An object containing maps of related 
 *   objects by their ref path strings. In particular, including the places will enrich locations.
 *   `locationsPrimary_full` and `locationsOther_full` will be added.
 */
export const enrichMember = (member, refPathMap) => {
  if (!member || member.isEnriched) return member;

  const hasMap = Object.keys(refPathMap || {}).length;

  const locationsPrimary_full = hasMap && member.locationsPrimary
    ? member.locationsPrimary.map(placeRef => refPathMap[placeRef]).filter(l => l)
    : null;
  
  const locationsOther_full = hasMap && member.locationsOther
    ? member.locationsOther.map(placeRef => refPathMap[placeRef]).filter(l => l)
    : null;

  Object.assign(member, {
    isEnriched: true,
    
    ref: `members/${member.id}`,
    profilePath: `/@${member.number}`,

    name: member.nameInternal || member.nameExternal || member.number,

    level_icon: memberLevelMap[member.level]?.icon || 'graph_5',
    level_label: memberLevelMap[member.level]?.label || 'Network Member',

    profile: enrichImageObject(member.profile),
    banner: enrichImageObject(member.banner),

    colorPrimary: member.colorPrimary || DEFAULT_COLOR_PRIMARY,

    locationsPrimary_full,
    locationsOther_full,
  });

  return member;
}

/**
 * This is a somewhat lightweight enrichMember, intended for enriching items from a member search
 * cache. It assumes that the `profile` field is a flat string of a firebase storage key already.
 * 
 * It returns the original object with the following additions:
 * - ref: A firestore reference string for the member (ex: `members/0KMscGXbeK4hMvlnt0UZ`)
 * - url: The relative URL to the member's profile page (ex: `/@Q0123`)
 * - level_icon: The icon (in the material library) representing the member's level.
 * - level_label: The label for the member's level (ex: `Network Member`)
 * - profile: The fully qualified URL to the member's profile image. This overwrites the prior
 *   value of `profile` in the original object and assumes that the original value is a firebase
 *   storage key (not an object).
 */
export const enrichMemberSearchItem = (member) => {
  if (member.isEnriched) return member;

  Object.assign(member, {
    ...member,

    isEnriched: true,

    ref: `members/${member.id}`,
    url: `/@${member.number}`,
    level_icon: memberLevelMap[member.level]?.icon || 'graph_5',
    level_label: memberLevelMap[member.level]?.label || 'Network Member',
    profile: getPublicFirestoreUrl(member.profile),
  });

  return member;
}
